import { useEffect, useMemo, useRef, useState } from 'react';
import styles from "../../../styles/igsosaAdminManagement.module.css";
import ConfirmModal from '../../../shared/components/ConfirmModal';
import { requesturl } from '../../../shared/utils/requesturl';

const PROJECTS_API = `${requesturl}api-projects.php`;
const GALLERY_API = `${requesturl}api-gallery.php`;
const ALUMNI_EVENTS_API = `${requesturl}alumnievents.php`;
const MAX_GALLERY_FILES = 30;
const MAX_GALLERY_FILE_SIZE_MB = 2;
const ACCEPTED_IMAGE_TYPES = 'image/*,.jpg,.jpeg,.png,.jfif,.webp,.heic,.heif';
const ALLOWED_IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'jfif', 'webp', 'heic', 'heif'];

const emptyProjectForm = {
    project_title: '',
    project_description: '',
    project_images: []
};

const emptyGalleryForm = {
    image_names: '',
    image_timeline: '',
    image_files: []
};

const emptyEventForm = {
    title: '',
    startDate: '',
    startTime: '',
    endDate: '',
    endTime: '',
    color: '#3b82f6'
};

const emptyConfirmDelete = {
    isOpen: false,
    itemId: null,
    itemType: '',
    itemName: ''
};

const emptySearchTerms = {
    projects: '',
    gallery: '',
    events: ''
};

const getFileExtension = (fileName = '') => {
    const parts = fileName.toLowerCase().split('.');
    return parts.length > 1 ? parts.pop() : '';
};

const isAllowedImageFile = (file) => {
    if (!file?.name) {
        return false;
    }

    return ALLOWED_IMAGE_EXTENSIONS.includes(getFileExtension(file.name));
};

const AdminDashboard = ({ onLogout }) => {
        document.title = "IGSOSA - Admin Dashboard";
    const [activeTab, setActiveTab] = useState('projects');
    const [projects, setProjects] = useState([]);
    const [galleries, setGalleries] = useState([]);
    const [events, setEvents] = useState([]);

    const [projectsPage, setProjectsPage] = useState(1);
    const [galleriesPage, setGalleriesPage] = useState(1);
    const [eventsPage, setEventsPage] = useState(1);
    const itemsPerPage = 5;

    const [projectForm, setProjectForm] = useState(emptyProjectForm);
    const [galleryForm, setGalleryForm] = useState(emptyGalleryForm);
    const [eventForm, setEventForm] = useState(emptyEventForm);

    const [confirmDelete, setConfirmDelete] = useState(emptyConfirmDelete);
    const [searchTerms, setSearchTerms] = useState(emptySearchTerms);

    const [existingTimelines, setExistingTimelines] = useState([]);
    const [showNewTimelineInput, setShowNewTimelineInput] = useState(false);
    const [newTimeline, setNewTimeline] = useState('');

    const [projectsLoading, setProjectsLoading] = useState(false);
    const [galleriesLoading, setGalleriesLoading] = useState(false);
    const [eventsLoading, setEventsLoading] = useState(false);
    const [projectSubmitting, setProjectSubmitting] = useState(false);
    const [gallerySubmitting, setGallerySubmitting] = useState(false);
    const [message, setMessage] = useState('');

    const projectFileInputRef = useRef(null);
    const galleryFileInputRef = useRef(null);

    useEffect(() => {
        fetchProjects();
        fetchGalleries();
        fetchEvents();
    }, []);

    const fetchProjects = async () => {
        try {
            setProjectsLoading(true);
            const response = await fetch(PROJECTS_API);
            const text = await response.text();
            const data = JSON.parse(text);

            if (data.success) {
                setProjects(data.data || []);
                setProjectsPage(1);
            } else {
                setMessage('Error fetching projects: ' + data.error);
            }
        } catch (err) {
            console.error('Projects fetch error:', err);
            setMessage('Error fetching projects: ' + err.message);
        } finally {
            setProjectsLoading(false);
        }
    };

    const fetchGalleries = async () => {
        try {
            setGalleriesLoading(true);
            const response = await fetch(GALLERY_API);
            const text = await response.text();
            const data = JSON.parse(text);

            if (data.success) {
                const galleryItems = data.data || [];
                setGalleries(galleryItems);
                setGalleriesPage(1);
                setExistingTimelines([...new Set(galleryItems.map((item) => item.image_timeline))].sort());
            } else {
                setMessage('Error fetching galleries: ' + data.error);
            }
        } catch (err) {
            console.error('Gallery fetch error:', err);
            setMessage('Error fetching galleries: ' + err.message);
        } finally {
            setGalleriesLoading(false);
        }
    };

    const fetchEvents = async () => {
        try {
            setEventsLoading(true);
            const response = await fetch(ALUMNI_EVENTS_API);
            const text = await response.text();
            const data = JSON.parse(text);

            if (Array.isArray(data)) {
                setEvents(data);
                setEventsPage(1);
            } else {
                setMessage('Error fetching events: Invalid response format');
            }
        } catch (err) {
            console.error('Events fetch error:', err);
            setMessage('Error fetching events: ' + err.message);
        } finally {
            setEventsLoading(false);
        }
    };

    const handleProjectFormChange = (e) => {
        const { name, value, files } = e.target;

        if (name === 'project_images' && files) {
            const selectedFiles = Array.from(files);
            const validFiles = selectedFiles.filter(isAllowedImageFile);

            setProjectForm((prev) => ({
                ...prev,
                project_images: validFiles
            }));

            if (validFiles.length !== selectedFiles.length) {
                setMessage('Some selected files were skipped. Please use jpg, jpeg, png, jfif, webp, heic, or heif images.');
            } else {
                setMessage('');
            }

            return;
        }

        setProjectForm((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const handleGalleryFormChange = (e) => {
        const { name, value, files } = e.target;

        if (name === 'image_files' && files) {
            const selectedFiles = Array.from(files);
            const validFiles = selectedFiles.filter(isAllowedImageFile);

            setGalleryForm((prev) => ({
                ...prev,
                image_files: validFiles
            }));

            if (validFiles.length !== selectedFiles.length) {
                setMessage('Some selected files were skipped. Please use jpg, jpeg, png, jfif, webp, heic, or heif images.');
            } else {
                setMessage('');
            }

            return;
        }

        setGalleryForm((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const handleEventFormChange = (e) => {
        const { name, value } = e.target;
        setEventForm((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSearchChange = (tab, value) => {
        setSearchTerms((prev) => ({
            ...prev,
            [tab]: value
        }));

        if (tab === 'projects') {
            setProjectsPage(1);
        } else if (tab === 'gallery') {
            setGalleriesPage(1);
        } else {
            setEventsPage(1);
        }
    };

    const handleAddNewTimeline = () => {
        const trimmedTimeline = newTimeline.trim();

        if (!trimmedTimeline) {
            return;
        }

        setExistingTimelines((prev) => {
            const nextTimelines = new Set(prev);
            nextTimelines.add(trimmedTimeline);
            return Array.from(nextTimelines).sort();
        });

        setGalleryForm((prev) => ({
            ...prev,
            image_timeline: trimmedTimeline
        }));
        setNewTimeline(trimmedTimeline);
        setShowNewTimelineInput(false);
    };

    const buildDateTime = (date, time) => {
        if (!date) return '';
        return time ? `${date}T${time}` : date;
    };

    const isValidDateString = (value) => {
        if (!value) return false;
        return !Number.isNaN(Date.parse(value));
    };

    const handleCreateProject = async () => {
        try {
            if (projectForm.project_images.length === 0) {
                setMessage('Please select at least one valid project image');
                return;
            }

            setProjectSubmitting(true);
            setMessage('Uploading project images, please wait...');

            const formData = new FormData();
            formData.append('project_title', projectForm.project_title);
            formData.append('project_description', projectForm.project_description);

            projectForm.project_images.forEach((file, index) => {
                formData.append(`images[${index}]`, file);
            });

            const response = await fetch(PROJECTS_API, {
                method: 'POST',
                body: formData
            });

            const data = await response.json();

            if (data.success) {
                setMessage('Project created successfully!');
                setProjectForm(emptyProjectForm);
                if (projectFileInputRef.current) {
                    projectFileInputRef.current.value = '';
                }
                await fetchProjects();
                setActiveTab('projects');
            } else {
                setMessage(data.error || 'Error occurred');
            }
        } catch (err) {
            console.error('Error creating project:', err);
            setMessage('Error creating project: ' + err.message);
        } finally {
            setProjectSubmitting(false);
        }
    };

    const handleCreateGallery = async () => {
        if (!galleryForm.image_timeline) {
            setMessage('Please select a timeline');
            return;
        }

        if (galleryForm.image_files.length > MAX_GALLERY_FILES) {
            setMessage(`You can upload a maximum of ${MAX_GALLERY_FILES} images at once`);
            return;
        }

        const oversizedFile = galleryForm.image_files.find(
            (file) => file.size > MAX_GALLERY_FILE_SIZE_MB * 1024 * 1024
        );

        if (oversizedFile) {
            setMessage(`Each gallery image must be ${MAX_GALLERY_FILE_SIZE_MB}MB or smaller`);
            return;
        }

        try {
            setGallerySubmitting(true);
            setMessage('Uploading gallery images, please wait...');

            const formData = new FormData();
            formData.append('image_names', galleryForm.image_names);
            formData.append('image_timeline', galleryForm.image_timeline);

            galleryForm.image_files.forEach((file) => {
                formData.append('images[]', file);
            });

            const response = await fetch(GALLERY_API, {
                method: 'POST',
                body: formData
            });

            const text = await response.text();
            if (!text || !text.trim()) {
                throw new Error('Empty response from server');
            }

            const data = JSON.parse(text);
            if (data.success) {
                const createdItems = Array.isArray(data.data) ? data.data.length : 1;
                setMessage(`${createdItems} gallery item${createdItems > 1 ? 's' : ''} created successfully!`);
                setGalleryForm(emptyGalleryForm);
                if (galleryFileInputRef.current) {
                    galleryFileInputRef.current.value = '';
                }
                await fetchGalleries();
                setActiveTab('gallery');
            } else {
                setMessage('Error: ' + data.error);
            }
        } catch (err) {
            console.error('Error creating gallery:', err);
            setMessage('Error creating gallery: ' + err.message);
        } finally {
            setGallerySubmitting(false);
        }
    };

    const handleCreateEvent = async () => {
        if (!eventForm.title.trim()) {
            setMessage('Event title is required');
            return;
        }

        if (!eventForm.startDate) {
            setMessage('Event start date is required');
            return;
        }

        const start = buildDateTime(eventForm.startDate, eventForm.startTime);
        const end = buildDateTime(eventForm.endDate, eventForm.endTime);

        if (!isValidDateString(start)) {
            setMessage('Event start date/time is invalid');
            return;
        }

        if (end && !isValidDateString(end)) {
            setMessage('Event end date/time is invalid');
            return;
        }

        if (end && Date.parse(end) < Date.parse(start)) {
            setMessage('Event end must be the same as or after the start');
            return;
        }

        try {
            const response = await fetch(ALUMNI_EVENTS_API, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    action: 'add',
                    title: eventForm.title.trim(),
                    start,
                    end,
                    color: eventForm.color || '#3b82f6'
                })
            });

            const text = await response.text();
            const data = JSON.parse(text);

            if (response.ok && data && !data.error) {
                setMessage('Event created successfully!');
                setEventForm(emptyEventForm);
                setEvents((prev) => {
                    const nextEvents = [...prev, data];
                    nextEvents.sort((a, b) => {
                        const aTime = Date.parse(a.start || '') || 0;
                        const bTime = Date.parse(b.start || '') || 0;
                        return bTime - aTime;
                    });
                    return nextEvents;
                });
            } else {
                setMessage('Error: ' + (data.error || 'Failed to create event'));
            }
        } catch (err) {
            console.error('Error creating event:', err);
            setMessage('Error creating event: ' + err.message);
        }
    };

    const handleDeleteProject = (id, title) => {
        setConfirmDelete({
            isOpen: true,
            itemId: id,
            itemType: 'project',
            itemName: title
        });
    };

    const handleDeleteGallery = (id, name) => {
        setConfirmDelete({
            isOpen: true,
            itemId: id,
            itemType: 'gallery',
            itemName: name
        });
    };

    const handleDeleteEvent = (id, title) => {
        setConfirmDelete({
            isOpen: true,
            itemId: id,
            itemType: 'event',
            itemName: title
        });
    };

    const handleConfirmDelete = async () => {
        const { itemId, itemType } = confirmDelete;

        try {
            let response;
            let data;

            if (itemType === 'event') {
                response = await fetch(ALUMNI_EVENTS_API, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        action: 'delete',
                        id: itemId
                    })
                });
                data = await response.json();
            } else {
                const endpoint = itemType === 'project'
                    ? `${PROJECTS_API}?id=${itemId}`
                    : `${GALLERY_API}?id=${itemId}`;

                response = await fetch(endpoint, {
                    method: 'DELETE'
                });
                data = await response.json();
            }

            if (response.ok && data.success) {
                if (itemType === 'project') {
                    setMessage('Project deleted successfully!');
                    await fetchProjects();
                    setActiveTab('projects');
                } else if (itemType === 'gallery') {
                    setMessage('Gallery item deleted successfully!');
                    await fetchGalleries();
                    setActiveTab('gallery');
                } else {
                    setMessage('Event deleted successfully!');
                    setEvents((prev) => prev.filter((event) => event.id !== itemId));
                }
            } else {
                setMessage('Error: ' + (data.error || 'Delete failed'));
            }
        } catch (err) {
            setMessage(`Error deleting ${itemType}: ` + err.message);
        } finally {
            setConfirmDelete(emptyConfirmDelete);
        }
    };

    const filterProjects = useMemo(() => {
        const query = searchTerms.projects.trim().toLowerCase();
        if (!query) return projects;

        return projects.filter((project) => {
            const imagesCount = Array.isArray(project.project_images) ? project.project_images.length.toString() : '0';
            return [
                project.project_id?.toString(),
                project.project_title,
                project.project_description,
                imagesCount,
                project.created_at
            ].some((value) => (value || '').toString().toLowerCase().includes(query));
        });
    }, [projects, searchTerms.projects]);

    const filterGalleries = useMemo(() => {
        const query = searchTerms.gallery.trim().toLowerCase();
        if (!query) return galleries;

        return galleries.filter((gallery) =>
            [
                gallery.image_id?.toString(),
                gallery.image_names,
                gallery.image_timeline,
                gallery.image_path,
                gallery.created_at
            ].some((value) => (value || '').toString().toLowerCase().includes(query))
        );
    }, [galleries, searchTerms.gallery]);

    const filterEvents = useMemo(() => {
        const query = searchTerms.events.trim().toLowerCase();
        if (!query) return events;

        return events.filter((event) =>
            [
                event.id?.toString(),
                event.title,
                event.start,
                event.end,
                event.color
            ].some((value) => (value || '').toString().toLowerCase().includes(query))
        );
    }, [events, searchTerms.events]);

    const getPaginatedItems = (items, page, perPage) => {
        const startIndex = (page - 1) * perPage;
        return items.slice(startIndex, startIndex + perPage);
    };

    const getTotalPages = (items, perPage) => Math.max(1, Math.ceil(items.length / perPage));

    const projectsToDisplay = getPaginatedItems(filterProjects, projectsPage, itemsPerPage);
    const galleriesToDisplay = getPaginatedItems(filterGalleries, galleriesPage, itemsPerPage);
    const eventsToDisplay = getPaginatedItems(filterEvents, eventsPage, itemsPerPage);
    const projectsTotalPages = getTotalPages(filterProjects, itemsPerPage);
    const galleriesTotalPages = getTotalPages(filterGalleries, itemsPerPage);
    const eventsTotalPages = getTotalPages(filterEvents, itemsPerPage);

    const deleteTitle = confirmDelete.itemType === 'project'
        ? 'Project'
        : confirmDelete.itemType === 'gallery'
            ? 'Gallery Item'
            : 'Event';

    return (
        <div className={styles.admincontainer}>
            <ConfirmModal
                isOpen={confirmDelete.isOpen}
                title={`Delete ${deleteTitle}`}
                message={`Are you sure you want to delete "${confirmDelete.itemName}"? This action cannot be undone.`}
                onConfirm={handleConfirmDelete}
                onCancel={() => setConfirmDelete(emptyConfirmDelete)}
            />

            <div className={styles.adminheader}>
                <h1>Admin Dashboard</h1>
                <button type="button" onClick={onLogout} className={styles.logoutbtn}>Logout</button>
            </div>

            {message && (
                <div className={styles.message}>
                    {message}
                    <button type="button" onClick={() => setMessage('')} className={styles.closemsg}>x</button>
                </div>
            )}

            <div className={styles.tabs}>
                <button type="button" className={`${styles.tab} ${activeTab === 'projects' ? styles.active : ''}`} onClick={() => { setActiveTab('projects'); fetchProjects(); }}>
                    Projects
                </button>
                <button type="button" className={`${styles.tab} ${activeTab === 'gallery' ? styles.active : ''}`} onClick={() => { setActiveTab('gallery'); fetchGalleries(); }}>
                    Gallery
                </button>
                <button type="button" className={`${styles.tab} ${activeTab === 'events' ? styles.active : ''}`} onClick={() => { setActiveTab('events'); fetchEvents(); }}>
                    Events
                </button>
            </div>

            <div className={styles.contentWrapper}>
                {activeTab === 'projects' && (
                    <div className={styles.tabcontent}>
                        <h2>Manage Projects</h2>

                        <div className={styles.form}>
                            <h3>Add New Project</h3>
                            <div className={styles.formrow}>
                                <div className={styles.formgroup}>
                                    <label>Project Title *</label>
                                    <input type="text" name="project_title" value={projectForm.project_title} onChange={handleProjectFormChange} placeholder="Enter project title" />
                                </div>
                            </div>
                            <div className={styles.formrow}>
                                <div className={styles.formgroup}>
                                    <label>Description *</label>
                                    <textarea name="project_description" value={projectForm.project_description} onChange={handleProjectFormChange} placeholder="Enter project description" rows="4" />
                                </div>
                            </div>
                            <div className={styles.formrow}>
                                <div className={styles.formgroup}>
                                    <label>Project Images (jpg, png, jpeg, jfif, webp, heic, heif) - Multiple files allowed</label>
                                    <input ref={projectFileInputRef} type="file" name="project_images" onChange={handleProjectFormChange} accept={ACCEPTED_IMAGE_TYPES} multiple />
                                    {projectForm.project_images.length > 0 && (
                                        <>
                                            <p className={styles.fileCount}>{projectForm.project_images.length} file(s) selected</p>
                                            <div className={styles.fileList}>
                                                {projectForm.project_images.map((file, index) => (
                                                    <span key={`${file.name}-${index}`} className={styles.fileListItem}>{file.name}</span>
                                                ))}
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                            <button type="button" onClick={handleCreateProject} className={styles.submitbtn} disabled={projectSubmitting}>
                                {projectSubmitting ? (
                                    <span className={styles.loadinglabel}>
                                        <span className={styles.spinner} aria-hidden="true" />
                                        Uploading Project...
                                    </span>
                                ) : 'Add Project'}
                            </button>
                        </div>

                        <div className={styles.listcontainer}>
                            <div className={styles.listheader}>
                                <h3>Projects List (Page {projectsPage} of {projectsTotalPages})</h3>
                                <input
                                    type="search"
                                    value={searchTerms.projects}
                                    onChange={(e) => handleSearchChange('projects', e.target.value)}
                                    placeholder="Search projects..."
                                    className={styles.searchinput}
                                />
                            </div>
                            {projectsLoading ? (
                                <p>Loading...</p>
                            ) : filterProjects.length === 0 ? (
                                <p>{searchTerms.projects ? 'No projects matched your search' : 'No projects found'}</p>
                            ) : (
                                <>
                                    <table className={styles.table}>
                                        <thead>
                                            <tr>
                                                <th>ID</th>
                                                <th>Title</th>
                                                <th>Description</th>
                                                <th>Images</th>
                                                <th>Created</th>
                                                <th>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {projectsToDisplay.map((project) => (
                                                <tr key={project.project_id}>
                                                    <td>{project.project_id}</td>
                                                    <td>{project.project_title}</td>
                                                    <td>{project.project_description.substring(0, 50)}...</td>
                                                    <td>{Array.isArray(project.project_images) ? project.project_images.length : 0}</td>
                                                    <td>{new Date(project.created_at).toLocaleDateString()}</td>
                                                    <td>
                                                        <button type="button" onClick={() => handleDeleteProject(project.project_id, project.project_title)} className={styles.deletebtn}>
                                                            Delete
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    {projectsTotalPages > 1 && (
                                        <div className={styles.pagination}>
                                            <button type="button" onClick={() => setProjectsPage(Math.max(1, projectsPage - 1))} disabled={projectsPage === 1} className={styles.paginationbtn}>
                                                {'<'} Previous
                                            </button>
                                            <span className={styles.pageinfo}>Page {projectsPage} of {projectsTotalPages}</span>
                                            <button type="button" onClick={() => setProjectsPage(Math.min(projectsTotalPages, projectsPage + 1))} disabled={projectsPage === projectsTotalPages} className={styles.paginationbtn}>
                                                Next {'>'}
                                            </button>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                )}

                {activeTab === 'gallery' && (
                    <div className={styles.tabcontent}>
                        <h2>Manage Gallery</h2>

                        <div className={styles.form}>
                            <h3>Add Gallery Item</h3>
                            <div className={styles.formrow}>
                                <div className={styles.formgroup}>
                                    <label>Base Image Name</label>
                                    <input type="text" name="image_names" value={galleryForm.image_names} onChange={handleGalleryFormChange} placeholder="Optional base name for uploaded images" />
                                </div>
                            </div>
                            <div className={styles.formrow}>
                                <div className={styles.formgroup}>
                                    <label>Timeline *</label>
                                    {showNewTimelineInput ? (
                                        <div className={styles.newtimelineinput}>
                                            <input type="text" value={newTimeline} onChange={(e) => setNewTimeline(e.target.value)} placeholder="Enter new timeline name" className={styles.timelineinput} />
                                            <button type="button" onClick={handleAddNewTimeline} className={styles.addbtn}>Add</button>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setShowNewTimelineInput(false);
                                                    setNewTimeline('');
                                                }}
                                                className={styles.cancelbtn}
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    ) : (
                                        <div>
                                            <select
                                                name="image_timeline"
                                                value={galleryForm.image_timeline}
                                                onChange={(e) => {
                                                    if (e.target.value === '__new__') {
                                                        setNewTimeline(galleryForm.image_timeline || '');
                                                        setShowNewTimelineInput(true);
                                                    } else {
                                                        setGalleryForm((prev) => ({
                                                            ...prev,
                                                            image_timeline: e.target.value
                                                        }));
                                                    }
                                                }}
                                                className={styles.select}
                                            >
                                                <option value="">-- Select a timeline --</option>
                                                {existingTimelines.map((timeline) => (
                                                    <option key={timeline} value={timeline}>{timeline}</option>
                                                ))}
                                                <option value="__new__">+ Add New Timeline</option>
                                            </select>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className={styles.formrow}>
                                <div className={styles.formgroup}>
                                    <label>Image Files (up to 30, 2MB each)</label>
                                    <input
                                        ref={galleryFileInputRef}
                                        type="file"
                                        name="image_files"
                                        onChange={handleGalleryFormChange}
                                        accept={ACCEPTED_IMAGE_TYPES}
                                        multiple
                                    />
                                    {galleryForm.image_files.length > 0 && (
                                        <>
                                            <p className={styles.fileCount}>{galleryForm.image_files.length} file(s) selected</p>
                                            <div className={styles.fileList}>
                                                {galleryForm.image_files.map((file, index) => (
                                                    <span key={`${file.name}-${index}`} className={styles.fileListItem}>{file.name}</span>
                                                ))}
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                            <button type="button" onClick={handleCreateGallery} className={styles.submitbtn} disabled={gallerySubmitting}>
                                {gallerySubmitting ? (
                                    <span className={styles.loadinglabel}>
                                        <span className={styles.spinner} aria-hidden="true" />
                                        Uploading Gallery...
                                    </span>
                                ) : 'Add Gallery Item'}
                            </button>
                        </div>

                        <div className={styles.listcontainer}>
                            <div className={styles.listheader}>
                                <h3>Gallery Items (Page {galleriesPage} of {galleriesTotalPages})</h3>
                                <input
                                    type="search"
                                    value={searchTerms.gallery}
                                    onChange={(e) => handleSearchChange('gallery', e.target.value)}
                                    placeholder="Search gallery..."
                                    className={styles.searchinput}
                                />
                            </div>
                            {galleriesLoading ? (
                                <p>Loading...</p>
                            ) : filterGalleries.length === 0 ? (
                                <p>{searchTerms.gallery ? 'No gallery items matched your search' : 'No gallery items found'}</p>
                            ) : (
                                <>
                                    <table className={styles.table}>
                                        <thead>
                                            <tr>
                                                <th>ID</th>
                                                <th>Name</th>
                                                <th>Timeline</th>
                                                <th>Image</th>
                                                <th>Created</th>
                                                <th>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {galleriesToDisplay.map((gallery) => (
                                                <tr key={gallery.image_id}>
                                                    <td>{gallery.image_id}</td>
                                                    <td>{gallery.image_names}</td>
                                                    <td>{gallery.image_timeline}</td>
                                                    <td>{gallery.image_path ? 'Yes' : 'No'}</td>
                                                    <td>{new Date(gallery.created_at).toLocaleDateString()}</td>
                                                    <td>
                                                        <button type="button" onClick={() => handleDeleteGallery(gallery.image_id, gallery.image_names)} className={styles.deletebtn}>
                                                            Delete
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    {galleriesTotalPages > 1 && (
                                        <div className={styles.pagination}>
                                            <button type="button" onClick={() => setGalleriesPage(Math.max(1, galleriesPage - 1))} disabled={galleriesPage === 1} className={styles.paginationbtn}>
                                                {'<'} Previous
                                            </button>
                                            <span className={styles.pageinfo}>Page {galleriesPage} of {galleriesTotalPages}</span>
                                            <button type="button" onClick={() => setGalleriesPage(Math.min(galleriesTotalPages, galleriesPage + 1))} disabled={galleriesPage === galleriesTotalPages} className={styles.paginationbtn}>
                                                Next {'>'}
                                            </button>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                )}

                {activeTab === 'events' && (
                    <div className={styles.tabcontent}>
                        <h2>Manage Alumni Events</h2>

                        <div className={styles.form}>
                            <h3>Add New Event</h3>
                            <div className={styles.formrow}>
                                <div className={styles.formgroup}>
                                    <label>Event Title *</label>
                                    <input type="text" name="title" value={eventForm.title} onChange={handleEventFormChange} placeholder="Enter event title" />
                                </div>
                            </div>
                            <div className={styles.formrow}>
                                <div className={styles.formgroup}>
                                    <label>Start Date *</label>
                                    <input type="date" name="startDate" value={eventForm.startDate} onChange={handleEventFormChange} />
                                </div>
                                <div className={styles.formgroup}>
                                    <label>Start Time</label>
                                    <input type="time" name="startTime" value={eventForm.startTime} onChange={handleEventFormChange} />
                                </div>
                            </div>
                            <div className={styles.formrow}>
                                <div className={styles.formgroup}>
                                    <label>End Date</label>
                                    <input type="date" name="endDate" value={eventForm.endDate} onChange={handleEventFormChange} />
                                </div>
                                <div className={styles.formgroup}>
                                    <label>End Time</label>
                                    <input type="time" name="endTime" value={eventForm.endTime} onChange={handleEventFormChange} />
                                </div>
                            </div>
                            <div className={styles.formrow}>
                                <div className={styles.formgroup}>
                                    <label>Event Color</label>
                                    <input type="color" name="color" value={eventForm.color} onChange={handleEventFormChange} />
                                </div>
                            </div>
                            <button type="button" onClick={handleCreateEvent} className={styles.submitbtn}>Add Event</button>
                        </div>

                        <div className={styles.listcontainer}>
                            <div className={styles.listheader}>
                                <h3>Events List (Page {eventsPage} of {eventsTotalPages})</h3>
                                <input
                                    type="search"
                                    value={searchTerms.events}
                                    onChange={(e) => handleSearchChange('events', e.target.value)}
                                    placeholder="Search events..."
                                    className={styles.searchinput}
                                />
                            </div>
                            {eventsLoading ? (
                                <p>Loading...</p>
                            ) : filterEvents.length === 0 ? (
                                <p>{searchTerms.events ? 'No events matched your search' : 'No events found'}</p>
                            ) : (
                                <>
                                    <table className={styles.table}>
                                        <thead>
                                            <tr>
                                                <th>ID</th>
                                                <th>Title</th>
                                                <th>Start</th>
                                                <th>End</th>
                                                <th>Color</th>
                                                <th>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {eventsToDisplay.map((event) => (
                                                <tr key={event.id}>
                                                    <td>{event.id}</td>
                                                    <td>{event.title}</td>
                                                    <td>{event.start || '-'}</td>
                                                    <td>{event.end || '-'}</td>
                                                    <td>{event.color || '-'}</td>
                                                    <td>
                                                        <button type="button" onClick={() => handleDeleteEvent(event.id, event.title)} className={styles.deletebtn}>
                                                            Delete
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    {eventsTotalPages > 1 && (
                                        <div className={styles.pagination}>
                                            <button type="button" onClick={() => setEventsPage(Math.max(1, eventsPage - 1))} disabled={eventsPage === 1} className={styles.paginationbtn}>
                                                {'<'} Previous
                                            </button>
                                            <span className={styles.pageinfo}>Page {eventsPage} of {eventsTotalPages}</span>
                                            <button type="button" onClick={() => setEventsPage(Math.min(eventsTotalPages, eventsPage + 1))} disabled={eventsPage === eventsTotalPages} className={styles.paginationbtn}>
                                                Next {'>'}
                                            </button>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminDashboard;
